import type { WagerResponse, WagerStatus } from '@gamble-chess/contracts';
import type { JSX } from 'react';

import { useWagerAction } from '../api/stakes.ts';
import {
  describePolicy,
  describeWagerOutcome,
  describeWagerParties,
  platformLabels,
  wagerActionLabels,
  wagerStatusLabels,
} from '../format.ts';
import { Alert } from '../ui/Alert.tsx';
import { Badge, type BadgeTone } from '../ui/Badge.tsx';
import { Entry } from '../ui/EntryList.tsx';
import { hintClass, linkClass, primaryButtonClass, secondaryButtonClass } from '../ui/styles.ts';
import { TierExplanation } from './TierExplanation.tsx';

const statusTones: Readonly<Record<WagerStatus, BadgeTone>> = {
  proposed: 'warning',
  active: 'accent',
  settled: 'neutral',
  declined: 'neutral',
  cancelled: 'neutral',
};

/** One wager, with whatever the reader is allowed to do about it. */
export function WagerEntry({ wager }: { readonly wager: WagerResponse }): JSX.Element {
  const act = useWagerAction();
  const pairing = `${wager.playerUsername} vs ${wager.opponentUsername}`;
  const { settlement } = wager;

  return (
    <Entry
      lead={
        <>
          {pairing}{' '}
          <span className="font-normal text-ink-muted">on {platformLabels[wager.platform]}</span>
        </>
      }
      meta={
        <>
          <span className="text-sm">
            {describeWagerParties(wager)} · {describePolicy(wager.policy)}
          </span>
          {settlement === null ? null : (
            <span className={hintClass}>
              <a href={settlement.gameUrl} className={linkClass} rel="noreferrer">
                {describeWagerOutcome(wager, settlement.outcome)}
              </a>
            </span>
          )}
          <TierExplanation policy={wager.policy} />
          {act.isError ? <Alert>{act.error.message}</Alert> : null}
        </>
      }
      trailing={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={statusTones[wager.status]}>{wagerStatusLabels[wager.status]}</Badge>
          {wager.actions.map((action) => (
            <button
              key={action}
              type="button"
              onClick={() => {
                act.mutate({ id: wager.id, action });
              }}
              disabled={act.isPending}
              aria-label={`${wagerActionLabels[action]} wager ${pairing}`}
              className={action === 'accept' ? primaryButtonClass : secondaryButtonClass}
            >
              {wagerActionLabels[action]}
            </button>
          ))}
        </div>
      }
    />
  );
}
